import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  SvgIcon,
  SvgIconProps,
  Typography,
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import { useRouter } from 'next/router';
import { useState } from 'react';
import useAuth from '@modules/auth/hooks/api/useAuth';
import Routes from '@common/defs/routes';
import { safeLocalStorage } from '@common/utils/storage';

interface FormationButtonProps {
  size?: number;
}

const HeartHandshakeIcon = (props: SvgIconProps) => (
  <SvgIcon
    {...props}
    viewBox="0 0 24 24"
    sx={{
      fill: 'none',
      stroke: 'currentColor',
      strokeWidth: 1.9,
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      ...props.sx,
    }}
  >
    <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
    <path d="M12 5 9.04 7.96a2.17 2.17 0 0 0 0 3.08c.82.82 2.13.85 3 .07l2.07-1.9a2.82 2.82 0 0 1 3.79 0l2.96 2.66" />
    <path d="m18 15-2-2" />
    <path d="m15 18-2-2" />
  </SvgIcon>
);

const FormationButton = ({ size = 35 }: FormationButtonProps) => {
  const router = useRouter();
  const { user } = useAuth();
  const [teaserOpen, setTeaserOpen] = useState(false);

  const isSubscribed = (user?.activeSubscriptions?.length ?? 0) > 0;

  const handleClick = () => {
    if (isSubscribed) {
      const token = safeLocalStorage.getItem('authToken');
      const url = `https://espace.allo-clients.com/api/sso/enter?token=${encodeURIComponent(
        token ?? ''
      )}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      setTeaserOpen(true);
    }
  };

  const handleCloseTeaser = () => setTeaserOpen(false);

  const handleGoToPro = () => {
    setTeaserOpen(false);
    router.push(Routes.Common.Pro);
  };

  return (
    <>
      <Button
        onClick={handleClick}
        aria-label="Espace Allo Clients"
        sx={{
          background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 60%, #1d4ed8 100%)',
          borderRadius: '500px',
          boxShadow:
            '0 4px 14px 0 rgba(59, 130, 246, 0.45), 0 0 0 1px rgba(29, 78, 216, 0.25) inset',
          padding: size * 0.025,
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          width: size,
          minWidth: size,
          height: size,
          '&:hover': {
            filter: 'brightness(1.08)',
            boxShadow:
              '0 6px 20px 0 rgba(59, 130, 246, 0.6), 0 0 0 1px rgba(29, 78, 216, 0.3) inset',
          },
        }}
      >
        <HeartHandshakeIcon
          sx={{ color: '#fff', filter: 'drop-shadow(0 1px 2px rgba(30, 58, 138, 0.5))' }}
        />
      </Button>

      <Dialog open={teaserOpen} onClose={handleCloseTeaser} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pr: 6, fontWeight: 600 }}>
          Espace Allo Clients
          <IconButton
            aria-label="Fermer"
            onClick={handleCloseTeaser}
            sx={{ position: 'absolute', right: 12, top: 12 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', color: '#3b82f6' }}>
              <HeartHandshakeIcon sx={{ fontSize: 64 }} />
            </Box>
            <Typography variant="body1" sx={{ textAlign: 'center', fontWeight: 500 }}>
              Réservé aux abonnés.
            </Typography>
            <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
              Ressources, outils et communauté pour développer sa pratique et gagner en visibilité.
              Quelques exemples parmi beaucoup d'autres :
            </Typography>
            <Box
              component="ul"
              sx={{
                listStyle: 'none',
                p: 0,
                m: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
                alignSelf: 'center',
              }}
            >
              {[
                'Demandes de séance',
                'Site internet',
                'Avis Google',
                'Réseaux sociaux',
                'Tutos',
                "Espace d'échange privé",
              ].map((item) => (
                <Box
                  key={item}
                  component="li"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: 'text.primary',
                    fontSize: '0.9rem',
                  }}
                >
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor: '#3b82f6',
                      flexShrink: 0,
                    }}
                  />
                  {item}
                </Box>
              ))}
              <Box
                component="li"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: 'text.secondary',
                  fontSize: '0.9rem',
                  fontStyle: 'italic',
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: 'transparent',
                    flexShrink: 0,
                  }}
                />
                et bien plus...
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={handleCloseTeaser} variant="text">
            Plus tard
          </Button>
          <Button
            onClick={handleGoToPro}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { filter: 'brightness(1.08)' },
            }}
          >
            Découvrir les formules
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FormationButton;
